import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getUrlName, getAPPID, isRedesignFlow } from '../Helpers/index';
import { NSE_STEPS, AAL_STEPS, getSuppressedStepperBackTarget, shouldSuppressStepper } from './constants';
import * as FuncCompConstants from '../../../pages/FuncComp/Constants';
import { SHOW_STREAMLINE_MINI_CART } from '../../../services/Cart/Constants';
import Stepper from './Stepper/index';
import CartSummary from './CartSummary/index';
// Cart open is delegated to the global mini-cart hook exposed by MiniCartFooter.
import {
  StickyWrapper,
  MobileFixedWrapper,
  BarContainer,
  BackButton,
  BackCartDivider,
  SuppressedSpacer,
  MobilePricingWrapper,
  MobilePricingItems,
  MobilePricingItem,
  MobilePricingLabel,
  MobilePricingValue,
} from './OrderProgressBarRedesign.styles';

/* ── Helpers ─────────────────────────────────────────────────────────── */

const getSteps = (isAAL) => (isAAL ? AAL_STEPS : NSE_STEPS);

const getCurrentStepIndex = (steps, pageName) =>
  steps.findIndex((step) => step.pages.includes(pageName));

/* ── Component ───────────────────────────────────────────────────────── */

const OrderProgressBarRedesign = ({ flowType: flowTypeOverride }) => {
  const dispatch = useDispatch();
  /* ── Page / flag / suppression ──────────────────────────────────── */
  const pageName = getUrlName(window.location.pathname);
  const redesignFlowEnabled = isRedesignFlow(pageName);
  const isAAL = flowTypeOverride ? flowTypeOverride === 'AAL' : getAPPID() === 'AAL';
  const searchParams = new URLSearchParams(window.location.search);
  const isStepperSuppressed = shouldSuppressStepper(pageName, searchParams);

  /* ── Steps / current index ──────────────────────────────────────── */
  const steps = useMemo(() => getSteps(isAAL), [isAAL]);
  const currentIndex = useMemo(() => getCurrentStepIndex(steps, pageName), [steps, pageName]);

  /* ── Redux ───────────────────────────────────────────────────────── */
  const cartPrices = useSelector((state) => {
    const orderView = state?.cartDetails?.cartDetailsJson?.orderDetailsView;
    const checkoutOrder = state?.checkout?.orderDetails;
    return {
      today: orderView?.dueTodayAmount ?? checkoutOrder?.totalDueToday ?? 0,
      monthly: orderView?.dueMonthlyAmount ?? checkoutOrder?.totalMonthlyDue ?? 0,
      savings: orderView?.dueMonthlyDiscountAmount ?? checkoutOrder?.dueMonthlyDiscountAmount ?? 0,
    };
  });

  /* ── Cart drawer ─────────────────────────────────────────────────── */
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Trigger mini-cart open via Redux so this works without any global window hook.
  const handleCartIconClick = () => {
    dispatch({ type: SHOW_STREAMLINE_MINI_CART, value: true });
    dispatch({ type: FuncCompConstants.IS_MINICART_SUMMARY_OPENED, value: true });
    setIsCartOpen(true);
  };

  /* ── Mobile scroll-based sticky ─────────────────────────────────── */
  const barRef = useRef(null);
  const thresholdRef = useRef(null);
  const [isMobileFixed, setIsMobileFixed] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const processScroll = () => {
      if (window.innerWidth > 767) {
        setIsMobileFixed(false);
        return;
      }
      const rect = barRef.current?.getBoundingClientRect?.();
      if (rect && thresholdRef.current === null) {
        thresholdRef.current = rect.top + window.scrollY;
      }
      if (thresholdRef.current !== null) {
        setIsMobileFixed(window.scrollY >= thresholdRef.current);
      }
    };

    let rafId = null;
    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        processScroll();
      });
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  /* ── Guard ───────────────────────────────────────────────────────── */
  if (!redesignFlowEnabled || (!isStepperSuppressed && currentIndex < 0)) {
    return null;
  }

  /* ── Back navigation (suppressed sub-pages) ─────────────────────── */
  const handleBackClick = () => {
    const backTarget = getSuppressedStepperBackTarget(pageName, searchParams);
    if (backTarget) {
      navigate(`/sales/home/${backTarget}`);
      return;
    }
    navigate(-1);
  };

  /* ── Cart visibility ─────────────────────────────────────────────── */
  const hasCartData =
    Number(cartPrices.today) > 0 ||
    Number(cartPrices.monthly) > 0 ||
    Number(cartPrices.savings) > 0;

  /* ── Bar content ─────────────────────────────────────────────────── */
  const renderBarContent = () => (
    <BarContainer $isStepperSuppressed={isStepperSuppressed}>

      {/* Order progress stepper (hidden on suppressed sub-pages) */}
      {!isStepperSuppressed && (
        <Stepper
          steps={steps}
          currentIndex={currentIndex}
          pageName={pageName}
          navigate={navigate}
        />
      )}

      {/* Back CTA (suppressed sub-pages only: Perks/Services, Shipping, Appointment) */}
      {isStepperSuppressed && (
        <BackButton
          type="button"
          onClick={handleBackClick}
          data-testid="progress-back-button"
          data-track={JSON.stringify({ type: 'link', name: 'Back - progressbar nav' })}
        >
          <span className="back-chevron" aria-hidden="true">&lsaquo;</span>
          <span>Back</span>
        </BackButton>
      )}

      {/* Mobile-only divider between Back CTA and cart (suppressed pages) */}
      {isStepperSuppressed && <BackCartDivider aria-hidden="true" />}

      {/* Desktop spacer that pushes the cart to the far right (suppressed pages) */}
      {isStepperSuppressed && <SuppressedSpacer aria-hidden="true" />}

      {/* Cart summary — shown only when cart data exists */}
      {hasCartData && (
        <>
          {/* Desktop: price chips + cart icon */}
          <CartSummary
            onCartIconClick={handleCartIconClick}
            isCartOpen={isCartOpen}
            displayMode="desktop"
          />

          {/* Mobile: price chips inline with cart icon */}
          <MobilePricingWrapper $isStepperSuppressed={isStepperSuppressed}>
            <MobilePricingItems $isStepperSuppressed={isStepperSuppressed}>
              <MobilePricingItem>
                <MobilePricingLabel>Due today</MobilePricingLabel>
                <MobilePricingValue data-testid="mobile-cart-summary-today">
                  ${Number(cartPrices.today).toFixed(2)}
                </MobilePricingValue>
              </MobilePricingItem>
              <MobilePricingItem>
                <MobilePricingLabel>Est monthly</MobilePricingLabel>
                <MobilePricingValue data-testid="mobile-cart-summary-monthly">
                  ${Number(cartPrices.monthly).toFixed(2)}
                </MobilePricingValue>
              </MobilePricingItem>
              {Number(cartPrices.savings) > 0 && (
                <MobilePricingItem>
                  <MobilePricingLabel isSavings>Savings</MobilePricingLabel>
                  <MobilePricingValue isSavings data-testid="mobile-cart-summary-savings">
                    ${Number(cartPrices.savings).toFixed(2)}/mo
                  </MobilePricingValue>
                </MobilePricingItem>
              )}
            </MobilePricingItems>
            <CartSummary
              onCartIconClick={handleCartIconClick}
              isCartOpen={isCartOpen}
              displayMode="mobile"
            />
          </MobilePricingWrapper>
        </>
      )}

    </BarContainer>
  );

  /* ── Render ──────────────────────────────────────────────────────── */
  return (
    <>
      {/* Desktop sticky + mobile inline bar */}
      <StickyWrapper
        ref={barRef}
        data-testid="order-progress-bar-redesign"
        role="banner"
        $hideOnMobile={isMobileFixed}
      >
        {renderBarContent()}
      </StickyWrapper>

      {/* Mobile: fixed clone that appears after the user scrolls past the inline bar */}
      {isMobileFixed && (
        <MobileFixedWrapper
          data-testid="order-progress-bar-redesign-fixed"
          role="banner"
          aria-label="Order progress"
        >
          {renderBarContent()}
        </MobileFixedWrapper>
      )}
    </>
  );
};

OrderProgressBarRedesign.propTypes = {
  /** Explicitly set the flow ('NSE' | 'AAL'). Defaults to reading the APPID cookie. */
  flowType: PropTypes.oneOf(['NSE', 'AAL']),
};

OrderProgressBarRedesign.defaultProps = {
  flowType: undefined,
};

export default OrderProgressBarRedesign;
